(function () {
  const EDGE_GUARD = 24;
  const LOCK_DISTANCE = 10;
  const HORIZONTAL_BIAS = 1.2;
  const RESIST = 0.35;
  const EXIT_MS = 280;
  const TOAST_MS = 5000;
  const INTERACTIVE = 'button, a, input, select, textarea, label, summary, details';

  let addsInFlight = 0;

  // UpCart intercepts every /cart/add request and opens its drawer. Swipe adds must stay in the feed, so skip UpCart's handling only while one of our adds is in flight.
  function hookUpcart() {
    if (window.__shSwipeUpcartHooked) return;
    window.__shSwipeUpcartHooked = true;
    const previous = window.upcartShouldSkipAddToCart;
    window.upcartShouldSkipAddToCart = function (request) {
      if (addsInFlight > 0) return true;
      return typeof previous === 'function' ? previous(request) : false;
    };
  }

  function track(action, detail) {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({ event: 'swipe_collection', swipe_action: action, ...detail });
  }

  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const [whole, fraction] = value.split('.');
    const withCommas = Number(whole).toLocaleString('en-US');
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas.replaceAll(',', '.') + ',' + fraction)
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + fraction)
      .replace(/<[^<>]*>/g, '');
  }

  class SwipeCollection {
    constructor(root) {
      this.root = root;
      this.threshold = (Number.parseFloat(root.dataset.threshold) || 28) / 100;
      this.moneyFormat = root.dataset.moneyFormat;
      this.giftConfig = this.readGiftConfig();
      this.text = {
        hintAdd: root.dataset.hintAdd || '',
        hintSubscribe: root.dataset.hintSubscribe || '',
        stampPass: root.dataset.stampPass || '',
        stampPassRelease: root.dataset.stampPassRelease || '',
        stampAdd: root.dataset.stampAdd || '',
        stampAddRelease: root.dataset.stampAddRelease || '',
      };
      this.storageKey = 'sh-swipe-collection-v1:' + root.dataset.collectionId;
      this.cards = Array.from(root.querySelectorAll('[data-card]')).map((el) => this.readCard(el));
      this.byId = new Map(this.cards.map((c) => [c.id, c]));
      this.cart = null;
      this.addQueue = Promise.resolve();
      this.drag = null;
      this.toastTimer = null;
      this.lastFocus = null;
      this.viewer = null;
      this.state = this.loadState();
      this.reconcileOrder();
      this.bind();
      this.render();
    }

    start() {
      this.wireUpcartEvents();
      return this.refreshCart();
    }

    readGiftConfig() {
      try {
        return this.root.dataset.giftConfig ? JSON.parse(this.root.dataset.giftConfig) : null;
      } catch {
        return null;
      }
    }

    giftsApply(card, variant) {
      if (!card.data.gifts || !this.giftConfig) return false;
      if (this.giftConfig.appliesTo === 'subscription_only') return Boolean(variant.planId);
      if (this.giftConfig.appliesTo === 'otp_only') return !variant.planId;
      return true;
    }

    // Mirrors the product page (ctr-free-gift.js): the gift lines are nested under the product line and both carry _shipping_offer.
    lineRef(card, variant) {
      const item = { id: variant.id, quantity: 1 };
      if (variant.planId) item.selling_plan = variant.planId;
      const ref = { item, variantId: variant.id, planId: variant.planId || null, shippingOffer: null, giftItems: [] };
      const config = this.giftConfig;
      if (!this.giftsApply(card, variant) || !config.addToCart || !config.variantIds.length) return ref;
      ref.shippingOffer = config.shippingOffer;
      item.properties = { _shipping_offer: config.shippingOffer };
      ref.giftItems = config.variantIds.map((id) => ({
        id,
        quantity: 1,
        parent_id: variant.id,
        properties: { _offer: config.propertyValue, _shipping_offer: config.shippingOffer },
      }));
      return ref;
    }

    isGiftLine(item) {
      return Boolean(this.giftConfig) && this.giftConfig.variantIds.includes(item.variant_id);
    }

    // The store's "Free Gift" discount covers one gift per order; a second gift line is charged at full price.
    async restoreGift() {
      const config = this.giftConfig;
      if (!config?.addToCart || !config.variantIds.length) return;
      const cart = await this.fetchCart();
      if (cart.items.some((i) => this.isGiftLine(i))) return;
      const parent = cart.items.find((i) => {
        if (this.isGiftLine(i) || i.properties?._shipping_offer !== config.shippingOffer) return false;
        if (config.appliesTo === 'subscription_only') return Boolean(this.planOf(i));
        if (config.appliesTo === 'otp_only') return !this.planOf(i);
        return true;
      });
      if (!parent) return;
      const items = config.variantIds.map((id) => ({
        id,
        quantity: 1,
        parent_id: parent.variant_id,
        properties: { _offer: config.propertyValue, _shipping_offer: config.shippingOffer },
      }));
      addsInFlight++;
      try {
        await fetch(this.root.dataset.cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
      } finally {
        addsInFlight--;
      }
    }

    matchesLine(item, ref) {
      const offer = item.properties?._shipping_offer || null;
      return item.variant_id === ref.variantId && this.planOf(item) === ref.planId && offer === ref.shippingOffer;
    }

    readCard(el) {
      const data = JSON.parse(el.querySelector('[data-card-data]').textContent);
      return {
        el,
        id: String(data.id),
        data,
        track: el.querySelector('[data-track]'),
        media: el.querySelector('.custom-swipe-collection-media'),
        segments: Array.from(el.querySelectorAll('.custom-swipe-collection-segment')),
        counter: el.querySelector('[data-counter]'),
        status: el.querySelector('[data-status]'),
        inBagPill: el.querySelector('[data-inbag-pill]'),
        gallery: el.querySelector('[data-gallery]'),
        gifts: el.querySelector('[data-gifts]'),
        pending: false,
      };
    }

    loadState() {
      const fallback = { order: [], decisions: {}, variants: {}, plans: {}, gal: {}, tipDismissed: false, firstAddDone: false };
      try {
        const saved = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
        return saved ? { ...fallback, ...saved } : fallback;
      } catch {
        return fallback;
      }
    }

    saveState() {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch {
        // Storage can be full or blocked (private mode); progress just isn't saved.
      }
    }

    reconcileOrder() {
      const ids = this.cards.map((c) => c.id);
      const kept = this.state.order.filter((id) => ids.includes(id));
      ids.forEach((id) => {
        if (!kept.includes(id)) kept.push(id);
      });
      this.state.order = kept;
      Object.keys(this.state.decisions).forEach((id) => {
        if (!ids.includes(id)) delete this.state.decisions[id];
      });
    }

    bind() {
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onKeyDown = this.handleDocumentKey.bind(this);

      this.cards.forEach((card) => {
        card.el.addEventListener('pointerdown', (e) => this.handlePointerDown(card, e));
        card.el.addEventListener('keydown', (e) => this.handleCardKey(card, e));
        card.el.querySelectorAll('[data-variant-id]').forEach((btn) => {
          btn.addEventListener('click', () => this.selectVariant(card, btn.dataset.variantId));
        });
        card.el.querySelectorAll('[data-plan]').forEach((btn) => {
          btn.addEventListener('click', () => this.selectPlan(card, btn.dataset.plan));
        });
        card.el.addEventListener('dragstart', (e) => e.preventDefault());
        card.el.addEventListener(
          'touchmove',
          (e) => {
            if (this.drag?.card === card && this.drag.lock === 'x' && e.cancelable) e.preventDefault();
          },
          { passive: false },
        );
      });

      this.root.addEventListener('click', (e) => {
        const target = e.target.closest('[data-action]');
        if (!target || !this.root.contains(target)) return;
        const card = target.closest('[data-card]');
        const current = card ? this.byId.get(card.dataset.productId) : null;
        switch (target.dataset.action) {
          case 'dismiss-tip':
            this.state.tipDismissed = true;
            this.saveState();
            this.render();
            break;
          case 'open-skipped':
            this.openSkipped(target);
            break;
          case 'open-bag':
            this.openBag();
            break;
          case 'reset':
            this.reset();
            break;
          case 'restart':
            this.restart();
            break;
          case 'details':
            if (current) this.openDetails(current, target);
            break;
          case 'gift':
            this.openGift(target.dataset.giftIndex, target);
            break;
          case 'enlarge':
            if (current) this.openViewer(current, target);
            break;
          case 'hide-toast':
            this.hideToast();
            break;
          case 'close-sheet':
            this.closeSheet();
            break;
          case 'close-viewer':
            this.closeViewer();
            break;
          case 'viewer-prev':
            this.stepViewer(-1);
            break;
          case 'viewer-next':
            this.stepViewer(1);
            break;
        }
      });

      window.addEventListener('pointermove', this.onPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
      document.addEventListener('keydown', this.onKeyDown);
    }

    destroy() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      document.removeEventListener('keydown', this.onKeyDown);
      clearTimeout(this.toastTimer);
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    q(selector) {
      return this.root.querySelector(selector);
    }

    feed() {
      return this.state.order.map((id) => this.byId.get(id)).filter((c) => c && !this.state.decisions[c.id]);
    }

    current() {
      return this.feed()[0] || null;
    }

    variantOf(card) {
      const chosen = this.state.variants[card.id];
      const variants = card.data.variants;
      const match = variants.find((v) => String(v.id) === String(chosen) && v.available);
      const variant = match || variants.find((v) => v.id === card.data.defaultVariantId) || variants[0];
      if (!variant.planId || this.state.plans[card.id] !== 'once') return variant;
      return {
        ...variant,
        subscription: variant,
        planId: null,
        planName: null,
        priceText: variant.onceText,
        compareText: variant.onceCompareText,
        savePercent: 0,
      };
    }

    intervalOf(planName) {
      return (planName || '').replace(/^deliver\s+/i, '').toLowerCase();
    }

    inBag(card) {
      if (!this.cart) return false;
      return this.cart.items.some((item) => String(item.product_id) === card.id);
    }

    passedCards() {
      return this.state.order
        .map((id) => this.byId.get(id))
        .filter((c) => c && this.state.decisions[c.id] === 'passed');
    }

    rightBlockReason(card) {
      if (this.inBag(card)) return 'inbag';
      return null;
    }

    render() {
      const current = this.current();
      const total = this.cards.length;
      const explored = total - this.feed().length;

      const tip = this.q('[data-tip]');
      if (tip) tip.hidden = this.state.tipDismissed;

      this.q('[data-progress-text]').textContent = explored + ' of ' + total + ' explored';
      this.q('[data-progress-bar]').style.width = (total ? (explored / total) * 100 : 0) + '%';
      this.q('[data-skipped-label]').textContent = 'Skipped (' + this.passedCards().length + ')';
      this.q('[data-reset]').hidden = explored === 0;

      this.cards.forEach((card) => {
        const isCurrent = card === current;
        if (card.el.hidden === isCurrent) {
          card.el.hidden = !isCurrent;
          if (isCurrent) this.enter(card);
        }
        this.renderCard(card);
      });

      const next = this.feed()[1];
      if (next) {
        const img = next.el.querySelector('img');
        if (img) img.loading = 'eager';
      }

      const hasCart = !!(this.cart && this.cart.item_count > 0);
      this.q('[data-hints]').hidden = !current;
      this.q('[data-end="bag"]').hidden = !!current || !hasCart;
      this.q('[data-end="empty"]').hidden = !!current || hasCart;

      if (current) {
        const reason = this.rightBlockReason(current);
        const variant = this.variantOf(current);
        let hint = (variant.planId && this.text.hintSubscribe) || this.text.hintAdd;
        if (reason === 'inbag') hint = 'Already in your bag';
        this.q('[data-hint-right]').textContent = hint;
      }

      this.renderBagBar();
    }

    renderPlans(card, variant) {
      const plans = card.el.querySelector('[data-plans]');
      if (!plans) return;
      const subscription = variant.subscription || variant;
      const hasPlan = Boolean(subscription.planId);
      plans.hidden = !hasPlan;
      const summary = card.el.querySelector('[data-sub-summary]');
      summary.hidden = !variant.planId;
      if (!hasPlan) return;
      plans.querySelectorAll('[data-plan]').forEach((btn) => {
        const checked = (btn.dataset.plan === 'sub') === Boolean(variant.planId);
        btn.setAttribute('aria-checked', checked ? 'true' : 'false');
        btn.tabIndex = checked ? 0 : -1;
      });
      const interval = this.intervalOf(subscription.planName);
      plans.querySelector('[data-plan-sub]').textContent = 'Delivered and billed ' + interval;
      plans.querySelector('[data-plan-price="sub"]').textContent = subscription.priceText;
      plans.querySelector('[data-plan-price="once"]').textContent = subscription.onceText;
      summary.querySelector('[data-sub-today]').textContent = subscription.priceText;
      summary.querySelector('[data-sub-delivered]').textContent = subscription.title === 'Default Title' ? card.data.title : subscription.title;
      summary.querySelector('[data-sub-renews]').textContent = subscription.priceText + ' ' + interval;
      const giftsApply = this.giftsApply(card, variant);
      summary.querySelectorAll('[data-sub-gifts]').forEach((el) => {
        el.hidden = !giftsApply;
      });
    }

    renderIncludes(card, variant) {
      const qty = card.el.querySelector('[data-include-qty]');
      if (qty && variant.title !== 'Default Title') qty.textContent = variant.title;
      const image = card.el.querySelector('.custom-swipe-collection-include-image');
      const src = variant.image || card.data.image;
      if (image && src && image.getAttribute('src') !== src) {
        image.removeAttribute('srcset');
        image.src = src;
      }
    }

    selectPlan(card, plan) {
      const value = plan === 'once' ? 'once' : 'sub';
      if ((this.state.plans[card.id] || 'sub') === value) return;
      if (value === 'once') this.state.plans[card.id] = 'once';
      else delete this.state.plans[card.id];
      this.saveState();
      this.setStatus(card, null);
      this.render();
      track('plan_change', { product_id: card.id, purchase_type: value });
    }

    openGift(index, trigger) {
      const template = this.q('[data-gift-template="' + index + '"]');
      if (!template) return;
      const body = document.createElement('div');
      body.className = 'custom-swipe-collection-gift-sheet';
      body.appendChild(template.content.cloneNode(true));
      track('gift_view', { gift_index: Number(index) });
      this.openSheet('Your free extra', body, trigger);
    }

    renderCard(card) {
      const variant = this.variantOf(card);
      card.el.querySelector('[data-price]').textContent = variant.priceText;
      const compare = card.el.querySelector('[data-compare]');
      compare.hidden = !variant.compareText;
      compare.textContent = variant.compareText || '';

      const note = card.el.querySelector('[data-price-note]');
      note.textContent = '';
      if (variant.planId) {
        if (variant.savePercent > 0) {
          const save = document.createElement('span');
          save.className = 'custom-swipe-collection-save';
          save.textContent = 'Save ' + variant.savePercent + '%';
          note.appendChild(save);
        }
        note.appendChild(
          document.createTextNode('Subscription · billed ' + variant.priceText + ' ' + this.intervalOf(variant.planName)),
        );
      } else {
        note.textContent = 'One-time purchase';
      }
      this.renderPlans(card, variant);
      this.renderIncludes(card, variant);

      card.el.querySelectorAll('[data-variant-id]').forEach((btn) => {
        const checked = btn.dataset.variantId === String(variant.id);
        btn.setAttribute('aria-checked', checked ? 'true' : 'false');
        btn.tabIndex = checked ? 0 : -1;
      });

      if (card.gifts) {
        const applies = this.giftsApply(card, variant);
        card.gifts.classList.toggle('is-inactive', !applies);
        card.gifts.querySelector('[data-gifts-note]').hidden = applies;
      }

      card.inBagPill.hidden = !this.inBag(card);
      this.renderGallery(card);
    }

    renderGallery(card, dragDx) {
      const count = card.data.images.length;
      if (!count) return;
      const index = Math.min(this.state.gal[card.id] || 0, count - 1);
      card.track.style.transform = 'translateX(' + -index * 100 + '%)';
      card.segments.forEach((s, i) => s.classList.toggle('is-active', i === index));
      if (card.counter) card.counter.textContent = index + 1 + ' / ' + count;
      if (dragDx === undefined) {
        const img = card.track.children[index]?.querySelector('img');
        if (img) img.loading = 'eager';
        this.applyTone(card, index);
      }
    }

    applyTone(card, index) {
      if (!card.media || card.segments.length === 0) return;
      const src = card.data.images[index]?.src;
      if (!src) return;
      this.toneOf(src).then((tone) => {
        if ((this.state.gal[card.id] || 0) !== index) return;
        if (tone === 'dark') card.media.dataset.tone = 'dark';
        else delete card.media.dataset.tone;
      });
    }

    toneOf(src) {
      this.tones = this.tones || new Map();
      if (!this.tones.has(src)) {
        this.tones.set(
          src,
          new Promise((resolve) => {
            const img = new Image();
            // Canvas reads need CORS; Shopify's image CDN sends Access-Control-Allow-Origin: *.
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(this.measureTone(img));
            img.onerror = () => resolve('light');
            img.src = src.replace(/([?&])width=\d+/, '$1width=48');
          }),
        );
      }
      return this.tones.get(src);
    }

    measureTone(img) {
      try {
        const size = 48;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, size, size);
        const scale = Math.min(size / img.naturalWidth, size / img.naturalHeight);
        const w = img.naturalWidth * scale;
        const h = img.naturalHeight * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        const { data } = ctx.getImageData(0, 0, size, 5);
        let sum = 0;
        for (let i = 0; i < data.length; i += 4) sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        return sum / (data.length / 4) / 255 < 0.55 ? 'dark' : 'light';
      } catch {
        // A tainted canvas (image served without CORS) keeps the default colours.
        return 'light';
      }
    }

    setGallery(card, index) {
      const count = card.data.images.length;
      if (!count) return;
      const next = Math.max(0, Math.min(count - 1, index));
      if (next === (this.state.gal[card.id] || 0)) return;
      this.state.gal[card.id] = next;
      this.saveState();
      this.renderGallery(card);
      track('gallery_change', { product_id: card.id, image_index: next });
    }

    enter(card) {
      if (this.reducedMotion()) return;
      card.el.classList.add('is-entering');
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          card.el.classList.remove('is-entering');
        }),
      );
      track('offer_impression', { product_id: card.id });
    }

    selectVariant(card, variantId) {
      const variant = card.data.variants.find((v) => String(v.id) === String(variantId));
      if (!variant?.available) return;
      this.state.variants[card.id] = variant.id;
      this.saveState();
      this.setStatus(card, null);
      this.render();
      track('configuration_change', { product_id: card.id, variant_id: variant.id });
    }

    setStatus(card, text, isError) {
      card.status.hidden = !text;
      card.status.textContent = text || '';
      card.status.classList.toggle('is-error', !!isError);
      if (isError) card.status.setAttribute('role', 'alert');
      else card.status.removeAttribute('role');
    }

    handlePointerDown(card, e) {
      if (card !== this.current() || card.pending || this.isOverlayOpen()) return;
      if (e.button > 0) return;
      if (e.target.closest(INTERACTIVE)) return;
      if (e.pointerType !== 'mouse' && (e.clientX < EDGE_GUARD || e.clientX > window.innerWidth - EDGE_GUARD)) return;
      const galleryRect =
        card.gallery && e.target.closest('[data-gallery]') ? card.gallery.getBoundingClientRect() : null;
      this.drag = {
        card,
        x0: e.clientX,
        y0: e.clientY,
        dx: 0,
        lock: null,
        width: card.el.offsetWidth,
        pointerId: e.pointerId,
        galleryRect,
      };
    }

    handlePointerMove(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.pointerId) return;
      const dx = e.clientX - d.x0;
      const dy = e.clientY - d.y0;
      if (!d.lock) {
        if (Math.hypot(dx, dy) < LOCK_DISTANCE) return;
        d.lock = Math.abs(dx) > Math.abs(dy) * HORIZONTAL_BIAS ? 'x' : 'y';
        if (d.lock === 'y') {
          this.drag = null;
          return;
        }
        try {
          d.card.el.setPointerCapture(d.pointerId);
        } catch {
          // Throws if the pointer was already released; the drag still works without capture.
        }
        d.card.el.classList.add('is-dragging');
        track('swipe_start', { product_id: d.card.id });
      }
      e.preventDefault();
      const blocked = dx > 0 && this.rightBlockReason(d.card);
      d.dx = blocked ? dx * RESIST : dx;
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.paintDrag(d));
    }

    paintDrag(d) {
      const limit = d.width * this.threshold;
      const progress = Math.min(1, Math.abs(d.dx) / limit);
      const rotate = this.reducedMotion() ? 0 : Math.max(-5, Math.min(5, (d.dx / d.width) * 12));
      d.card.el.style.transform = 'translateX(' + d.dx + 'px) rotate(' + rotate + 'deg)';
      const pass = d.card.el.querySelector('[data-stamp="pass"]');
      const add = d.card.el.querySelector('[data-stamp="add"]');
      pass.style.opacity = d.dx < 0 ? progress : 0;
      add.style.opacity = d.dx > 0 ? progress : 0;
      const past = progress >= 1;
      pass.querySelector('[data-stamp-text]').textContent = (past && this.text.stampPassRelease) || this.text.stampPass;
      add.querySelector('[data-stamp-text]').textContent = (past && this.text.stampAddRelease) || this.text.stampAdd;
    }

    resetDrag(card) {
      card.el.classList.remove('is-dragging');
      card.el.style.transform = '';
      card.el.querySelectorAll('[data-stamp]').forEach((s) => {
        s.style.opacity = 0;
      });
    }

    handlePointerUp(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.pointerId) return;
      this.drag = null;
      cancelAnimationFrame(this.raf);

      if (d.lock !== 'x') {
        if (!d.lock && d.galleryRect && e.type === 'pointerup') {
          const left = e.clientX - d.galleryRect.left < d.galleryRect.width / 2;
          this.setGallery(d.card, (this.state.gal[d.card.id] || 0) + (left ? -1 : 1));
        }
        return;
      }

      const limit = d.width * this.threshold;
      if (d.dx <= -limit) {
        this.pass(d.card, 'swipe');
      } else if (d.dx >= limit) {
        this.add(d.card, 'swipe').catch(() => {});
      } else {
        this.resetDrag(d.card);
        track('swipe_cancel', { product_id: d.card.id });
        const reason = d.dx > 0 && this.rightBlockReason(d.card);
        if (reason) this.explainBlocked(d.card);
      }
    }

    handleCardKey(card, e) {
      if (e.target !== card.el) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.pass(card, 'keyboard');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.add(card, 'keyboard').catch(() => {});
      }
    }

    handleDocumentKey(e) {
      if (this.viewer) {
        if (e.key === 'Escape') this.closeViewer();
        else if (e.key === 'ArrowLeft') this.stepViewer(-1);
        else if (e.key === 'ArrowRight') this.stepViewer(1);
        else if (e.key === 'Tab') this.trapFocus(e, this.q('[data-viewer]'));
        return;
      }
      if (!this.q('[data-sheet]').hidden) {
        if (e.key === 'Escape') this.closeSheet();
        else if (e.key === 'Tab') this.trapFocus(e, this.q('[data-sheet]'));
      }
    }

    trapFocus(e, container) {
      const focusable = Array.from(
        container.querySelectorAll('button:not([disabled]), a[href], [tabindex="0"]'),
      ).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    isOverlayOpen() {
      return !!this.viewer || !this.q('[data-sheet]').hidden;
    }

    explainBlocked(card) {
      this.toast({
        kind: 'info',
        message: 'Already in your bag',
        sub: 'Change the quantity in your bag.',
        action: 'Review bag',
        onAction: () => this.openBag(),
      });
      this.announce(card.data.title + ' is already in your bag.');
    }

    exit(card, direction, done) {
      if (this.reducedMotion()) {
        card.el.style.transition = 'opacity 200ms linear';
        card.el.style.opacity = '0';
      } else {
        card.el.style.transition = 'transform ' + EXIT_MS + 'ms cubic-bezier(0.4, 0, 0.2, 1)';
        card.el.style.transform =
          'translateX(' +
          (direction === 'right' ? 125 : -125) +
          '%) rotate(' +
          (direction === 'right' ? 8 : -8) +
          'deg)';
      }
      card.el.classList.remove('is-dragging');
      setTimeout(
        () => {
          card.el.style.transition = '';
          card.el.style.opacity = '';
          this.resetDrag(card);
          done();
        },
        this.reducedMotion() ? 200 : EXIT_MS,
      );
    }

    moveToFront(id) {
      const order = this.state.order.filter((x) => x !== id);
      const at = order.findIndex((x) => !this.state.decisions[x]);
      order.splice(at < 0 ? order.length : at, 0, id);
      this.state.order = order;
    }

    pass(card, source) {
      if (card !== this.current() || card.pending) return;
      this.exit(card, 'left', () => {
        this.state.decisions[card.id] = 'passed';
        this.saveState();
        this.render();
        this.focusCurrent();
        this.toast({
          kind: 'info',
          message: 'Passed for now',
          sub: 'It’s saved in Skipped.',
          action: 'Undo',
          onAction: () => this.undoPass(card),
        });
        this.announce('Passed for now. Moved to Skipped.');
      });
      track('offer_pass', { product_id: card.id, source });
    }

    undoPass(card) {
      delete this.state.decisions[card.id];
      this.moveToFront(card.id);
      this.saveState();
      this.hideToast();
      this.render();
      this.focusCurrent();
      this.announce(card.data.title + ' is back in the feed.');
      track('undo', { product_id: card.id, undo_of: 'pass' });
    }

    async add(card, source) {
      if (card.pending) return;
      const reason = this.rightBlockReason(card);
      if (reason) {
        if (card === this.current()) this.resetDrag(card);
        this.explainBlocked(card);
        return;
      }

      const variant = this.variantOf(card);
      const fromFeed = card === this.current();
      card.pending = true;
      this.announce('Adding ' + card.data.title + '…');
      track('add_attempt', { product_id: card.id, variant_id: variant.id, selling_plan: variant.planId, source });

      const ref = this.lineRef(card, variant);
      const request = this.enqueueAdd(ref);

      // The card leaves the feed as soon as it is swiped; the cart request runs behind the animation and a failed add brings the card back.
      let exited = Promise.resolve();
      if (fromFeed) {
        this.fly(card);
        exited = new Promise((resolve) => {
          this.exit(card, 'right', () => {
            this.state.decisions[card.id] = 'added';
            this.saveState();
            this.render();
            this.focusCurrent();
            resolve();
          });
        });
      } else {
        this.setStatus(card, 'Adding to your bag…');
      }

      let previousQty;
      try {
        previousQty = await request;
      } catch (error) {
        await exited;
        card.pending = false;
        if (fromFeed) {
          delete this.state.decisions[card.id];
          this.moveToFront(card.id);
          this.saveState();
          this.render();
          this.focusCurrent();
        }
        this.setStatus(card, 'Couldn’t add this product. Swipe right to try again.', true);
        this.toast({ kind: 'error', message: 'Couldn’t add this product. Try again.', sub: card.data.title });
        this.announce('Couldn’t add ' + card.data.title + '. Try again.');
        track('add_failure', {
          product_id: card.id,
          variant_id: variant.id,
          source,
          error: String(error?.message),
        });
        return false;
      }

      card.pending = false;
      this.setStatus(card, null);
      track('add_success', { product_id: card.id, variant_id: variant.id, selling_plan: variant.planId, source });
      await exited;

      if (!fromFeed) {
        this.state.decisions[card.id] = 'added';
        this.saveState();
        this.render();
      }
      const first = !this.state.firstAddDone;
      this.state.firstAddDone = true;
      const record = { card, ref, previousQty, fromFeed };
      const message = first ? 'It’s a match — added to your bag' : 'Added to your bag';
      this.toast({
        kind: 'add',
        message,
        sub: card.data.title + ' · ' + variant.priceText,
        action: 'Undo',
        onAction: () => this.undoAdd(record),
      });
      this.announce(message + '. ' + card.data.title + ', ' + variant.priceText + '.');
      this.pulseBag();
      return true;
    }

    // Adds run one at a time: each reads the cart first to decide whether the gift rides along and what quantity Undo restores.
    enqueueAdd(ref) {
      const run = this.addQueue.then(() => this.postAdd(ref));
      this.addQueue = run.catch(() => {});
      return run;
    }

    async postAdd(ref) {
      const before = await this.fetchCart();
      const previousLine = before.items.find((i) => this.matchesLine(i, ref));
      const previousQty = previousLine ? previousLine.quantity : 0;
      const giftItems = before.items.some((i) => this.isGiftLine(i)) ? [] : ref.giftItems;

      addsInFlight++;
      let response;
      let json;
      try {
        response = await fetch(this.root.dataset.cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [ref.item, ...giftItems], sections: 'cart-icon-bubble' }),
        });
        json = await response.json().catch(() => ({}));
      } finally {
        addsInFlight--;
      }
      if (!response.ok || json.status) throw new Error(json.description || json.message || 'Add failed');

      this.updateBubble(json.sections);
      const hasLine = (c) => c.items.some((i) => this.matchesLine(i, ref) && i.quantity > previousQty);
      this.refreshCart()
        .then(() => {
          if (this.cart && !hasLine(this.cart)) return this.settleCart(hasLine);
        })
        .catch(() => {});
      this.syncUpcart();
      return previousQty;
    }

    async undoAdd(record) {
      const { card, ref, previousQty, fromFeed } = record;
      this.hideToast();
      try {
        const json = await this.changeLine(ref, previousQty);
        this.updateBubble(json.sections);
        await this.refreshCart();
        this.syncUpcart();
        this.confirmQuantity(ref, previousQty)
          .then(() => this.restoreGift())
          .then(() => this.refreshCart())
          .then(() => this.syncUpcart())
          .catch(() => {});
        delete this.state.decisions[card.id];
        if (fromFeed) this.moveToFront(card.id);
        else this.state.decisions[card.id] = 'passed';
        this.saveState();
        this.render();
        this.focusCurrent();
        this.toast({
          kind: 'info',
          message: 'Undone',
          sub:
            previousQty > 0 ? 'Back to ' + previousQty + ' in your bag.' : card.data.title + ' removed from your bag.',
        });
        this.announce(
          'Undone. ' +
            card.data.title +
            (previousQty > 0 ? ' quantity back to ' + previousQty + '.' : ' removed from your bag.'),
        );
        track('undo', { product_id: card.id, undo_of: 'add' });
      } catch {
        await this.refreshCart();
        this.toast({
          kind: 'error',
          message: 'Couldn’t undo. Your bag hasn’t changed.',
          sub: 'Showing your current bag.',
          action: 'Retry',
          onAction: () => this.undoAdd(record),
        });
        this.announce('Couldn’t undo. Your bag hasn’t changed.');
        track('undo_failure', { product_id: card.id });
      }
    }

    reset() {
      const previous = structuredClone(this.state);
      this.state = {
        ...this.state,
        order: this.cards.map((c) => c.id),
        decisions: {},
        variants: {},
        plans: {},
        gal: {},
      };
      this.saveState();
      this.render();
      this.focusCurrent();
      this.toast({
        kind: 'info',
        message: 'Feed reset',
        sub: 'Your bag hasn’t changed.',
        action: 'Undo',
        onAction: () => {
          this.state = previous;
          this.saveState();
          this.hideToast();
          this.render();
          this.focusCurrent();
          track('reset_undo');
        },
      });
      this.announce('Feed reset. Your bag hasn’t changed.');
      track('reset');
    }

    restart() {
      Object.keys(this.state.decisions).forEach((id) => {
        if (this.state.decisions[id] === 'passed') delete this.state.decisions[id];
      });
      this.saveState();
      this.render();
      this.focusCurrent();
      track('restart');
    }

    focusCurrent() {
      const current = this.current();
      if (current && this.root.contains(document.activeElement)) current.el.focus({ preventScroll: true });
    }

    // The theme's gift rules (cart-offers.js) rewrite the cart after adds, which can change
    // line keys and positions, so the line is looked up fresh and retried once.
    async changeLine(ref, quantity) {
      let lastError;
      for (let attempt = 0; attempt < 2; attempt++) {
        const cart = await this.fetchCart();
        const line = cart.items.find((i) => this.matchesLine(i, ref));
        if (!line) throw new Error('Line not found');
        const response = await fetch(this.root.dataset.cartChangeUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity, sections: 'cart-icon-bubble' }),
        });
        const json = await response.json().catch(() => ({}));
        if (response.ok && !json.status) return json;
        lastError = new Error(json.description || 'Undo failed');
      }
      throw lastError;
    }

    // Other apps (UpCart rewards, Intelligems cart metafields) write to the cart right after an add; a write that overlaps ours can restore the old quantity, so check and repeat once.
    async confirmQuantity(ref, quantity) {
      await new Promise((resolve) => setTimeout(resolve, 900));
      const cart = await this.fetchCart();
      const line = cart.items.find((i) => this.matchesLine(i, ref));
      const current = line ? line.quantity : 0;
      if (current !== quantity) await this.changeLine(ref, quantity);
    }

    planOf(item) {
      return item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : null;
    }

    async fetchCart() {
      const response = await fetch(this.root.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart unavailable');
      return response.json();
    }

    async settleCart(isSettled) {
      for (let attempt = 0; attempt < 5; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await this.refreshCart();
        if (!this.cart || isSettled(this.cart)) return;
      }
    }

    async refreshCart() {
      try {
        this.cart = await this.fetchCart();
      } catch {
        return;
      }
      this.render();
    }

    wireUpcartEvents(attempt = 0) {
      if (typeof window.upcartSubscribeCartUpdated === 'function') {
        try {
          window.upcartSubscribeCartUpdated(() => this.refreshCart());
        } catch {
          // UpCart API failures must not break the swipe feed; the bag bar refreshes after our own adds.
        }
        return;
      }
      if (attempt < 40) setTimeout(() => this.wireUpcartEvents(attempt + 1), 250);
    }

    syncUpcart() {
      if (typeof window.upcartRefreshCart === 'function') {
        try {
          Promise.resolve(window.upcartRefreshCart()).catch(() => {});
        } catch {
          // Same as above: UpCart's drawer catches up on its next open.
        }
      }
    }

    updateBubble(sections) {
      const html = sections?.['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (!html || !bubble) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (parsed) bubble.innerHTML = parsed.innerHTML;
    }

    openBag() {
      track('bag_review');
      if (typeof window.upcartOpenCart === 'function') {
        window.upcartOpenCart();
        return;
      }
      window.location.href = this.root.dataset.cartUrl;
    }

    renderBagBar() {
      const bar = this.q('[data-bagbar]');
      const count = this.cart ? this.cart.item_count : 0;
      bar.hidden = count === 0;
      this.q('[data-toast]').classList.toggle('has-bagbar', count > 0);
      if (!count) return;
      this.q('[data-bag-count]').textContent = count + (count === 1 ? ' item' : ' items');
      this.q('[data-bag-total]').textContent = formatMoney(this.cart.total_price, this.moneyFormat);
    }

    pulseBag() {
      const inner = this.q('[data-bagbar-inner]');
      if (!inner || this.reducedMotion()) return;
      inner.classList.remove('is-pulsing');
      inner.getBoundingClientRect();
      inner.classList.add('is-pulsing');
    }

    fly(card) {
      if (this.reducedMotion()) return;
      const index = this.state.gal[card.id] || 0;
      const source = card.track.children[index]?.querySelector('img');
      const target = document.getElementById('cart-icon-bubble');
      if (!source || !target) return;
      const from = source.getBoundingClientRect();
      const to = target.getBoundingClientRect();
      if (!from.width || !to.width) return;
      const size = Math.min(from.width, from.height, 140);
      const ghost = document.createElement('img');
      ghost.src = source.currentSrc || source.src;
      ghost.alt = '';
      ghost.className = 'custom-swipe-collection-fly';
      ghost.style.width = size + 'px';
      ghost.style.height = size + 'px';
      ghost.style.left = from.left + (from.width - size) / 2 + 'px';
      ghost.style.top = from.top + (from.height - size) / 2 + 'px';
      this.root.appendChild(ghost);
      const dx = to.left + to.width / 2 - (from.left + from.width / 2);
      const dy = to.top + to.height / 2 - (from.top + from.height / 2);
      requestAnimationFrame(() => {
        ghost.style.transform = 'translate(' + dx + 'px, ' + dy + 'px) scale(0.15)';
        ghost.style.opacity = '0.3';
      });
      setTimeout(() => ghost.remove(), 560);
    }

    toast(options) {
      const wrap = this.q('[data-toast]');
      const inner = this.q('[data-toast-inner]');
      const action = this.q('[data-toast-action]');
      clearTimeout(this.toastTimer);
      inner.classList.toggle('is-error', options.kind === 'error');
      inner.classList.toggle('is-info', options.kind === 'info');
      this.q('[data-toast-message]').textContent = options.message;
      this.q('[data-toast-sub]').textContent = options.sub || '';
      action.hidden = !options.action;
      action.textContent = options.action || '';
      action.onclick = options.onAction
        ? () => {
            options.onAction();
          }
        : null;
      wrap.hidden = false;
      inner.style.animation = 'none';
      inner.getBoundingClientRect();
      inner.style.animation = '';
      this.toastTimer = setTimeout(() => this.hideToast(), options.kind === 'error' ? TOAST_MS * 1.6 : TOAST_MS);
    }

    hideToast() {
      clearTimeout(this.toastTimer);
      this.q('[data-toast]').hidden = true;
    }

    announce(text) {
      const region = this.q('[data-announce]');
      region.textContent = '';
      setTimeout(() => {
        region.textContent = text;
      }, 40);
    }

    openSheet(title, body, trigger) {
      this.lastFocus = trigger || document.activeElement;
      this.q('[data-sheet-title]').textContent = title;
      const container = this.q('[data-sheet-body]');
      container.innerHTML = '';
      container.appendChild(body);
      this.q('[data-sheet]').hidden = false;
      this.q('[data-sheet-overlay]').hidden = false;
      document.documentElement.style.overflow = 'hidden';
      this.q('[data-sheet] [data-action="close-sheet"]').focus();
    }

    closeSheet() {
      const sheet = this.q('[data-sheet]');
      if (sheet.hidden) return;
      sheet.hidden = true;
      this.q('[data-sheet-overlay]').hidden = true;
      document.documentElement.style.overflow = '';
      const back =
        this.lastFocus && document.contains(this.lastFocus) && this.lastFocus.offsetParent !== null
          ? this.lastFocus
          : this.current()?.el;
      if (back) back.focus({ preventScroll: true });
      this.lastFocus = null;
    }

    openSkipped(trigger) {
      track('skipped_open');
      this.openSheet('Skipped', this.buildSkipped(), trigger);
    }

    buildSkipped() {
      const list = document.createElement('div');
      const passed = this.passedCards();
      if (!passed.length) {
        const empty = document.createElement('p');
        empty.className = 'custom-swipe-collection-sheet-empty';
        empty.textContent = 'Nothing skipped yet. Products you pass appear here so you can come back to them.';
        list.appendChild(empty);
        return list;
      }
      list.style.display = 'flex';
      list.style.flexDirection = 'column';
      list.style.gap = '14px';
      passed.forEach((card) => {
        const variant = this.variantOf(card);
        const row = document.createElement('div');
        row.className = 'custom-swipe-collection-skipped-item';

        if (card.data.image) {
          const img = document.createElement('img');
          img.className = 'custom-swipe-collection-skipped-image';
          img.src = card.data.image;
          img.alt = '';
          row.appendChild(img);
        }

        const info = document.createElement('div');
        info.className = 'custom-swipe-collection-skipped-info';
        const name = document.createElement('span');
        name.className = 'custom-swipe-collection-skipped-name';
        name.textContent = card.data.title;
        const meta = document.createElement('span');
        meta.className = 'custom-swipe-collection-skipped-meta';
        meta.textContent = variant.title + ' · ' + variant.priceText + (variant.planId ? ' · Subscribe & save' : '');
        const actions = document.createElement('div');
        actions.className = 'custom-swipe-collection-skipped-actions';

        if (!this.rightBlockReason(card)) {
          const addBtn = document.createElement('button');
          addBtn.type = 'button';
          addBtn.className =
            'custom-swipe-collection-button custom-swipe-collection-button-cta custom-swipe-collection-button-small';
          addBtn.textContent = 'Add to bag';
          addBtn.addEventListener('click', async () => {
            addBtn.disabled = true;
            addBtn.textContent = 'Adding…';
            track('skipped_recover', { product_id: card.id, to: 'bag' });
            const ok = await this.add(card, 'skipped');
            if (ok) this.rebuildSkipped();
            else {
              addBtn.disabled = false;
              addBtn.textContent = 'Add to bag';
            }
          });
          actions.appendChild(addBtn);
        }

        const backBtn = document.createElement('button');
        backBtn.type = 'button';
        backBtn.className =
          'custom-swipe-collection-button custom-swipe-collection-button-outline custom-swipe-collection-button-small';
        backBtn.textContent = 'Back to feed';
        backBtn.addEventListener('click', () => {
          delete this.state.decisions[card.id];
          this.moveToFront(card.id);
          this.saveState();
          this.render();
          track('skipped_recover', { product_id: card.id, to: 'feed' });
          this.lastFocus = null;
          this.closeSheet();
          card.el.focus({ preventScroll: true });
        });
        actions.appendChild(backBtn);

        info.append(name, meta, actions);
        row.appendChild(info);
        list.appendChild(row);
      });
      return list;
    }

    rebuildSkipped() {
      const sheet = this.q('[data-sheet]');
      if (sheet.hidden) return;
      const container = this.q('[data-sheet-body]');
      container.innerHTML = '';
      container.appendChild(this.buildSkipped());
      this.q('[data-sheet] [data-action="close-sheet"]').focus();
    }

    openDetails(card, trigger) {
      const variant = this.variantOf(card);
      const body = document.createElement('div');
      body.style.display = 'flex';
      body.style.flexDirection = 'column';
      body.style.gap = '14px';

      const price = document.createElement('div');
      price.className = 'custom-swipe-collection-details-price';
      const strong = document.createElement('strong');
      strong.textContent = variant.priceText;
      const note = document.createElement('span');
      note.textContent =
        variant.title + (variant.planId ? ' · Subscribe & save · ' + variant.planName : ' · One-time purchase');
      price.append(strong, note);
      body.appendChild(price);

      const template = card.el.querySelector('[data-details-template]');
      if (template) body.appendChild(template.content.cloneNode(true));
      this.fillDetails(card, variant, body);

      track('details_open', { product_id: card.id });
      this.openSheet(card.data.title, body, trigger);
    }

    fillDetails(card, variant, body) {
      const included = body.querySelector('[data-details-included]');
      if (included) {
        const lines = [(variant.title === 'Default Title' ? '1 × ' : variant.title + ' · ') + card.data.title];
        if (this.giftsApply(card, variant)) {
          card.el.querySelectorAll('[data-gifts] .custom-swipe-collection-gift-name').forEach((el) => {
            lines.push('Free: ' + el.textContent.trim());
          });
        }
        lines.forEach((text) => {
          const p = document.createElement('p');
          p.textContent = text;
          included.appendChild(p);
        });
      }
      const terms = body.querySelector('[data-details-terms]');
      if (terms) {
        const subscription = variant.subscription || variant;
        const lines = [];
        if (subscription.planId) {
          lines.push('Subscription: ' + subscription.priceText + ' billed ' + this.intervalOf(subscription.planName) + '.');
          const subTerms = this.root.dataset.subscriptionTerms;
          if (subTerms) lines.push(subTerms);
        }
        lines.push('One-time purchase: charged once, ' + (subscription.onceText || subscription.priceText) + '.');
        lines.forEach((text) => {
          const p = document.createElement('p');
          p.textContent = text;
          terms.appendChild(p);
        });
      }
    }

    openViewer(card, trigger) {
      const images = card.data.images;
      if (!images.length) return;
      this.viewer = { card, index: this.state.gal[card.id] || 0, trigger };
      this.q('[data-viewer]').hidden = false;
      document.documentElement.style.overflow = 'hidden';
      this.renderViewer();
      this.q('[data-viewer] [data-action="close-viewer"]').focus();
      track('viewer_open', { product_id: card.id });
    }

    renderViewer() {
      const { card, index } = this.viewer;
      const images = card.data.images;
      const image = images[index];
      const img = this.q('[data-viewer-image]');
      img.src = image.src;
      img.alt = image.alt;
      this.q('[data-viewer-caption]').textContent = image.alt;
      this.q('[data-viewer-counter]').textContent = index + 1 + ' / ' + images.length;
      this.q('[data-action="viewer-prev"]').disabled = index === 0;
      this.q('[data-action="viewer-next"]').disabled = index === images.length - 1;
      const single = images.length < 2;
      this.q('[data-action="viewer-prev"]').hidden = single;
      this.q('[data-action="viewer-next"]').hidden = single;
    }

    stepViewer(delta) {
      if (!this.viewer) return;
      const count = this.viewer.card.data.images.length;
      const next = Math.max(0, Math.min(count - 1, this.viewer.index + delta));
      if (next === this.viewer.index) return;
      this.viewer.index = next;
      this.setGallery(this.viewer.card, next);
      this.renderViewer();
    }

    closeViewer() {
      if (!this.viewer) return;
      const trigger = this.viewer.trigger;
      this.viewer = null;
      this.q('[data-viewer]').hidden = true;
      document.documentElement.style.overflow = '';
      if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
    }
  }

  const instances = new Map();

  // The section is hidden on desktop, and the page can hide it too, so a feed starts only once its section is actually shown.
  const visibility = new ResizeObserver((entries) => {
    if (entries.some((entry) => entry.target.getClientRects().length)) initAll();
  });

  function initAll() {
    document.querySelectorAll('.custom-swipe-collection').forEach((root) => {
      if (root.dataset.initialised === 'true') return;
      if (!root.getClientRects().length) {
        visibility.observe(root);
        return;
      }
      visibility.unobserve(root);
      hookUpcart();
      root.dataset.initialised = 'true';
      const instance = new SwipeCollection(root);
      instances.set(root.dataset.sectionId, instance);
      instance.start().catch(() => {});
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', (event) => {
    const instance = instances.get(event.detail.sectionId);
    if (instance) instance.destroy();
    instances.delete(event.detail.sectionId);
    initAll();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const instance = instances.get(event.detail.sectionId);
    if (instance) instance.destroy();
    instances.delete(event.detail.sectionId);
  });
})();
